import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ThemeService } from './theme.service';
import { GoogleFontsService, GoogleFont } from './google-fonts.service';
import { ThemeVariable, VariableCategory } from './theme-variable.model';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-customizer',
  templateUrl: './customizer.component.html',
  styleUrls: ['./customizer.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class CustomizerComponent {
  categories$: Observable<VariableCategory[]>;
  googleFonts$: Observable<GoogleFont[]>;
  searchTerm: string = '';
  
  constructor(
    private themeService: ThemeService,
    private googleFontsService: GoogleFontsService
  ) {
    this.categories$ = this.themeService.theme$;
    this.googleFonts$ = this.googleFontsService.fonts$;
  }

  updateVariable(variable: ThemeVariable, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;

    if (variable.type === 'font') {
      // Load the font if it's a Google Font
      this.googleFontsService.loadFontToPage(newValue);
      // Format the font-family string with fallbacks
      const formattedValue = this.googleFontsService.formatFontFamily(newValue);
      this.themeService.updateVariable({
        ...variable,
        value: formattedValue
      });
    } else {
      this.themeService.updateVariable({
        ...variable,
        value: newValue
      });
    }
  }

  selectFont(variable: ThemeVariable, fontFamily: string): void {
    if (variable.type === 'font') {
      this.googleFontsService.loadFontToPage(fontFamily);
      const formattedValue = this.googleFontsService.formatFontFamily(fontFamily);
      this.themeService.updateVariable({
        ...variable,
        value: formattedValue
      });
    }
  }

  filterFonts(fonts: GoogleFont[] | null, searchTerm: string): GoogleFont[] {
    if (!fonts) return [];
    if (!searchTerm) return fonts;
    const term = searchTerm.toLowerCase();
    return fonts.filter(font => 
      font.family.toLowerCase().includes(term) || 
      font.category.toLowerCase().includes(term)
    );
  }

  resetTheme(): void {
    this.themeService.resetToDefault();
  }

  exportTheme(): void {
    const themeJson = this.themeService.exportTheme();
    const blob = new Blob([themeJson], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'theme.json';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async importTheme(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const text = await file.text();
      this.themeService.importTheme(text);
      input.value = '';
    }
  }

  getPreviewStyle(fontFamily: string): object {
    return { fontFamily };
  }
}